import { useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../../../shared/hooks/api';
import useMergeState from '../../../shared/hooks/useMergeState';
import { error, success } from '../../../utils/toast';
import { useNavigate } from 'react-router-dom';
import { PageContentWrapper } from '../../../shared/styled/PageContentStyle';
import Title from './Title';
import TabNavigation from './TabNavigation';
import BackButton from '../../../shared/components/BackButton';
import { AuthenticationContext } from '../../../provider/AuthenticationProvider';
import { ROLE } from '../../../data/enums';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';
import CancelModal from '../../../shared/components/CancelModal';

const initialUser = {
    is_active: true,
    role: 'SALE_STAFF',
};

const CreateUserProfile = () => {
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [{ isCreating }, createUser] = api.post('/users/');
    const [userProfile, setUserProfile] = useMergeState(initialUser);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    useEffect(() => {
        if (userProfile.name ||
            userProfile.email ||
            userProfile.phone ||
            userProfile.role ||
            userProfile.username ||
            userProfile.password ||
            userProfile.confirm_password ||
            userProfile.is_active !== true ||
            userProfile.role !== 'SALE_STAFF'
        ) {
            setIsModified(true);
        }
    }, [userProfile]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isModified, navigate]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setShowUnsavedDialog(true);
            setNavigationPath(path);
            return false;
        }

        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/users/");
    };  

    const handleSaveAndNavigate = async () => {
        setIsModified(false);
        navigate(navigationPath);
    };

    const handleDontSaveAndNavigate = () => {
        setIsModified(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleSubmit = () => {
        createUser(userProfile)
            .then(() => {
                success('Thành Công', 'Tạo mới tài khoản thành công');
                setIsModified(false);
                navigate(`/users`);
                return true;
            })
            .catch((err: any) => {
                error('Lỗi tạo mới tài khoản', err);
                return false;
            });
    };

    return (
        <PageContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title={'Tạo mới tài khoản'}
                setValidated={setValidated}
                handleSubmit={handleSubmit}
                isLoading={isCreating}
            />
            <TabNavigation validated={validated} requiredData={userProfile} mergeUser={setUserProfile} />
            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </PageContentWrapper>
    );
};

export default CreateUserProfile;
